/**
 * Common tour demos
 * Contains demo HTML templates for common UI components
 * Used across multiple modules
 */

import type { TourDemoType } from '../types'

/**
 * Generate common demo HTML
 * Returns empty string if demo is not a common demo
 */
export const getCommonDemoHTML = (demo?: TourDemoType): string => {
    if (!demo) return ''

    switch (demo) {
        case 'drag':
            return `
                <div class="tour-demo">
                    <div class="tour-demo-drag">
                        <div class="tour-demo-widget placeholder">A</div>
                        <div class="tour-demo-widget dragging">B</div>
                        <div class="tour-demo-widget">C</div>
                    </div>
                </div>
            `
        case 'resize':
            return `
                <div class="tour-demo">
                    <div class="tour-demo-resize">
                        <div class="tour-demo-widget">
                            Widget
                            <svg class="tour-demo-resize-handle" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M22 22L12 22M22 22L22 12M22 22L14 14"/>
                            </svg>
                        </div>
                    </div>
                </div>
            `
        case 'hide':
            return `
                <div class="tour-demo">
                    <div class="tour-demo-hide">
                        <div class="tour-demo-widget">Widget</div>
                        <div class="tour-demo-trash">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                            Thả để ẩn
                        </div>
                    </div>
                </div>
            `
        case 'contribute':
            return `
                <div class="tour-demo">
                    <div class="tour-demo-contribute">
                        <div class="tour-demo-plus-btn">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                                <path d="M12 5v14M5 12h14"/>
                            </svg>
                        </div>
                        <div class="tour-demo-form">
                            <div class="tour-demo-form-input long"></div>
                            <div class="tour-demo-form-input"></div>
                            <div class="tour-demo-form-btn"></div>
                        </div>
                    </div>
                </div>
            `
        case 'delete-modal':
            return `
                <div class="tour-demo">
                    <div class="tour-demo-modal delete">
                        <div class="tour-demo-modal-header">
                            <div class="tour-demo-header-left">
                                <svg class="tour-demo-modal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                                </svg>
                                <span class="tour-demo-modal-title">Xóa hóa đơn</span>
                            </div>
                            <svg class="tour-demo-modal-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </div>
                        <div class="tour-demo-modal-body">
                            <div class="tour-demo-modal-row">
                                <span class="tour-demo-modal-label">Lý do xóa *</span>
                                <div class="tour-demo-modal-input select">Chọn lý do...</div>
                            </div>
                            <div class="tour-demo-modal-row">
                                <span class="tour-demo-modal-label">Ghi chú</span>
                                <div class="tour-demo-modal-textarea">Nhập ghi chú chi tiết...</div>
                            </div>
                        </div>
                        <div class="tour-demo-modal-footer">
                            <button class="tour-demo-modal-btn cancel">Hủy</button>
                            <button class="tour-demo-modal-btn danger">Xóa</button>
                        </div>
                    </div>
                </div>
            `
        case 'pay-modal':
            return `
                <div class="tour-demo">
                    <div class="tour-demo-modal pay">
                        <div class="tour-demo-modal-header">
                            <div class="tour-demo-header-left">
                                <svg class="tour-demo-modal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
                                </svg>
                                <span class="tour-demo-modal-title">Thanh toán</span>
                            </div>
                            <svg class="tour-demo-modal-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </div>
                        <div class="tour-demo-modal-body">
                            <div class="tour-demo-modal-row">
                                <span class="tour-demo-modal-label">Phương thức *</span>
                                <div class="tour-demo-modal-input select">Chuyển khoản</div>
                            </div>
                            <div class="tour-demo-modal-row">
                                <span class="tour-demo-modal-label">Ngày thanh toán *</span>
                                <div class="tour-demo-modal-input">06/01/2026</div>
                            </div>
                        </div>
                        <div class="tour-demo-modal-footer">
                            <button class="tour-demo-modal-btn cancel">Hủy</button>
                            <button class="tour-demo-modal-btn success">Xác nhận</button>
                        </div>
                    </div>
                </div>
            `
        case 'send-mail':
            return `
                <div class="tour-demo">
                    <div class="tour-demo-modal mail">
                        <div class="tour-demo-modal-header">
                            <div class="tour-demo-header-left">
                                <svg class="tour-demo-modal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                                </svg>
                                <span class="tour-demo-modal-title">Gửi email</span>
                            </div>
                            <svg class="tour-demo-modal-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </div>
                        <div class="tour-demo-modal-body">
                            <div class="tour-demo-modal-row">
                                <span class="tour-demo-modal-label">Người nhận *</span>
                                <div class="tour-demo-modal-input">customer@email.com</div>
                            </div>
                            <div class="tour-demo-modal-row">
                                <span class="tour-demo-modal-label">CC</span>
                                <div class="tour-demo-modal-input">sale@company.vn</div>
                            </div>
                        </div>
                        <div class="tour-demo-modal-footer">
                            <button class="tour-demo-modal-btn cancel">Hủy</button>
                            <button class="tour-demo-modal-btn primary">Gửi</button>
                        </div>
                    </div>
                </div>
            `
        case 'add-update':
            return `
                <div class="tour-demo">
                    <div class="tour-demo-modal update">
                        <div class="tour-demo-modal-header">
                            <div class="tour-demo-header-left">
                                <svg class="tour-demo-modal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M12 5v14M5 12h14"/>
                                </svg>
                                <span class="tour-demo-modal-title">Thêm cập nhật</span>
                            </div>
                            <svg class="tour-demo-modal-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </div>
                        <div class="tour-demo-modal-body">
                            <div class="tour-demo-modal-row">
                                <span class="tour-demo-modal-label">Loại cập nhật *</span>
                                <div class="tour-demo-modal-input select">Chọn loại...</div>
                            </div>
                            <div class="tour-demo-modal-row">
                                <span class="tour-demo-modal-label">Mô tả</span>
                                <div class="tour-demo-modal-textarea">Nhập nội dung cập nhật...</div>
                            </div>
                        </div>
                        <div class="tour-demo-modal-footer">
                            <button class="tour-demo-modal-btn cancel">Hủy</button>
                            <button class="tour-demo-modal-btn purple">Lưu</button>
                        </div>
                    </div>
                </div>
            `
        case 'add-invoice-line':
            return `
                <div class="tour-demo">
                    <div class="tour-demo-modal invoice-line">
                        <div class="tour-demo-modal-header">
                            <div class="tour-demo-header-left">
                                <svg class="tour-demo-modal-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                    <path d="M12 5v14M5 12h14"/>
                                </svg>
                                <span class="tour-demo-modal-title">Thêm dòng hoá đơn</span>
                            </div>
                            <svg class="tour-demo-modal-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </div>
                        <div class="tour-demo-modal-body">
                            <div style="display: flex; gap: 8px; margin-bottom: 8px;">
                                <div class="tour-demo-modal-chip active">
                                    <div class="tour-demo-modal-checkbox-box checked"></div>
                                    Tự động tính
                                </div>
                                <div class="tour-demo-modal-chip">
                                    <div class="tour-demo-modal-checkbox-box"></div>
                                    Thêm giảm giá
                                </div>
                            </div>
                            <div class="tour-demo-modal-row">
                                <span class="tour-demo-modal-label">Dịch Vụ</span>
                                <div class="tour-demo-modal-service">
                                    <span>4467 - VNIS - ENTERPRISE</span>
                                    <svg class="tour-demo-modal-service-close" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                        <path d="M6 18L18 6M6 6l12 12"/>
                                    </svg>
                                </div>
                            </div>
                            <div class="tour-demo-modal-row">
                                <span class="tour-demo-modal-label">Tên chi tiết / Tên item</span>
                                <div class="tour-demo-modal-input">Tên chi tiết hoá đơn</div>
                            </div>
                            <div class="tour-demo-modal-grid">
                                <div class="tour-demo-modal-row">
                                    <span class="tour-demo-modal-label">Thông Số</span>
                                    <div class="tour-demo-modal-input">1</div>
                                </div>
                                <div class="tour-demo-modal-row">
                                    <span class="tour-demo-modal-label">Đơn giá</span>
                                    <div class="tour-demo-modal-input">0</div>
                                </div>
                                <div class="tour-demo-modal-row">
                                    <span class="tour-demo-modal-label">Giảm giá</span>
                                    <div class="tour-demo-modal-input">0</div>
                                </div>
                                <div class="tour-demo-modal-row">
                                    <span class="tour-demo-modal-label">Thành Tiền</span>
                                    <div class="tour-demo-modal-input">0</div>
                                </div>
                            </div>
                            <div class="tour-demo-modal-row">
                                <div class="tour-demo-modal-chip">
                                    <div class="tour-demo-modal-checkbox-box"></div>
                                    Thuế
                                </div>
                            </div>
                        </div>
                        <div class="tour-demo-modal-footer">
                            <button class="tour-demo-modal-btn cancel">Huỷ</button>
                            <button class="tour-demo-modal-btn primary">Lưu</button>
                        </div>
                    </div>
                </div>
            `
        case 'preview-mail':
            return `
                <div class="tour-demo" style="padding: 8px; min-height: 200px;">
                    <div style="background: white; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.15); width: 260px; overflow: hidden; position: relative;">
                        <!-- Download button -->
                        <div style="position: absolute; top: 8px; right: 8px; width: 50px; height: 18px; background: #1A237E; border-radius: 4px;"></div>
                        <!-- Header -->
                        <div style="padding: 10px 12px; border-bottom: 1px solid #e5e7eb; display: flex; justify-content: space-between; align-items: center;">
                            <div style="display: flex; align-items: center; gap: 6px;">
                                <div style="width: 20px; height: 20px; background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%); border-radius: 50%;"></div>
                                <div style="width: 50px; height: 10px; background: #1A237E; border-radius: 2px;"></div>
                            </div>
                            <div style="display: flex; flex-direction: column; align-items: flex-end; gap: 3px;">
                                <div style="width: 80px; height: 8px; background: #e5e7eb; border-radius: 2px;"></div>
                                <div style="width: 60px; height: 6px; background: #e5e7eb; border-radius: 2px;"></div>
                            </div>
                        </div>
                        <!-- Body -->
                        <div style="padding: 12px;">
                            <!-- Title -->
                            <div style="width: 200px; height: 12px; background: #dbeafe; border-radius: 3px; margin-bottom: 10px;"></div>
                            <!-- Greeting -->
                            <div style="width: 120px; height: 8px; background: #e5e7eb; border-radius: 2px; margin-bottom: 8px;"></div>
                            <!-- Content lines -->
                            <div style="margin-bottom: 12px;">
                                <div style="width: 100%; height: 6px; background: #e5e7eb; border-radius: 2px; margin-bottom: 4px;"></div>
                                <div style="width: 85%; height: 6px; background: #e5e7eb; border-radius: 2px;"></div>
                            </div>
                            <!-- Table -->
                            <div style="border: 1px solid #e5e7eb; border-radius: 4px; margin-bottom: 12px; overflow: hidden;">
                                <!-- Header row -->
                                <div style="display: grid; grid-template-columns: 20px 1fr 40px 50px; gap: 4px; padding: 6px 8px; background: #f3f4f6;">
                                    <div style="height: 8px; background: #d1d5db; border-radius: 2px;"></div>
                                    <div style="height: 8px; background: #d1d5db; border-radius: 2px;"></div>
                                    <div style="height: 8px; background: #d1d5db; border-radius: 2px;"></div>
                                    <div style="height: 8px; background: #d1d5db; border-radius: 2px;"></div>
                                </div>
                                <!-- Data row -->
                                <div style="display: grid; grid-template-columns: 20px 1fr 40px 50px; gap: 4px; padding: 6px 8px; border-bottom: 1px solid #f3f4f6;">
                                    <div style="height: 6px; background: #e5e7eb; border-radius: 2px;"></div>
                                    <div style="height: 6px; background: #e5e7eb; border-radius: 2px;"></div>
                                    <div style="height: 6px; background: #e5e7eb; border-radius: 2px;"></div>
                                    <div style="height: 6px; background: #e5e7eb; border-radius: 2px;"></div>
                                </div>
                                <!-- Total row -->
                                <div style="display: flex; justify-content: space-between; padding: 6px 8px; background: #f8fafc;">
                                    <div style="width: 60px; height: 8px; background: #1A237E; border-radius: 2px;"></div>
                                    <div style="width: 50px; height: 8px; background: #1A237E; border-radius: 2px;"></div>
                                </div>
                            </div>
                            <!-- Payment info -->
                            <div style="background: #f9fafb; padding: 8px; border-radius: 4px;">
                                <div style="width: 100px; height: 8px; background: #374151; border-radius: 2px; margin-bottom: 6px;"></div>
                                <div style="width: 150px; height: 6px; background: #e5e7eb; border-radius: 2px; margin-bottom: 4px;"></div>
                                <div style="width: 100px; height: 6px; background: #e5e7eb; border-radius: 2px; margin-bottom: 4px;"></div>
                                <div style="width: 130px; height: 6px; background: #e5e7eb; border-radius: 2px;"></div>
                            </div>
                        </div>
                        <!-- Footer -->
                        <div style="padding: 8px 12px; border-top: 3px solid #dc2626; background: #f9fafb;">
                            <div style="width: 140px; height: 6px; background: #e5e7eb; border-radius: 2px; margin-bottom: 4px;"></div>
                            <div style="width: 180px; height: 6px; background: #e5e7eb; border-radius: 2px;"></div>
                        </div>
                    </div>
                </div>
            `
        case 'select-dropdown':
            return `
                <div class="tour-demo" style="padding: 12px;">
                    <div style="width: 200px;">
                        <!-- Select input -->
                        <div style="background: white; border: 1px solid #3b82f6; border-radius: 6px; padding: 8px 12px; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);">
                            <span style="font-size: 12px; color: #374151;">Support Requests</span>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>
                        </div>
                        <!-- Dropdown options -->
                        <div style="background: white; border: 1px solid #e5e7eb; border-radius: 6px; margin-top: 4px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); overflow: hidden; animation: tour-modal-appear 2s ease-in-out infinite;">
                            <div style="padding: 8px 12px; background: #eff6ff; color: #1d4ed8; font-size: 11px; display: flex; align-items: center; gap: 6px;">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>
                                Support Requests
                            </div>
                            <div style="padding: 8px 12px; font-size: 11px; color: #374151;">Error Service</div>
                            <div style="padding: 8px 12px; font-size: 11px; color: #374151;">Service Requests</div>
                            <div style="padding: 8px 12px; font-size: 11px; color: #374151;">Change Requests</div>
                        </div>
                    </div>
                </div>
            `
        case 'customer-select':
            return `
                <div class="tour-demo" style="padding: 12px;">
                    <div style="width: 220px;">
                        <!-- Search input -->
                        <div style="background: white; border: 1px solid #3b82f6; border-radius: 6px; padding: 8px 12px; display: flex; align-items: center; gap: 8px; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                            <span style="font-size: 12px; color: #374151;">Tìm khách hàng...</span>
                        </div>
                        <!-- Results dropdown -->
                        <div style="background: white; border: 1px solid #e5e7eb; border-radius: 6px; margin-top: 4px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); overflow: hidden; animation: tour-modal-appear 2s ease-in-out infinite;">
                            <div style="padding: 10px 12px; border-bottom: 1px solid #f3f4f6; display: flex; align-items: center; gap: 10px;">
                                <div style="width: 28px; height: 28px; background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 10px; font-weight: 600;">AB</div>
                                <div>
                                    <div style="font-size: 11px; font-weight: 500; color: #1f2937;">ABC Company</div>
                                    <div style="font-size: 9px; color: #6b7280;">contact@abc.vn</div>
                                </div>
                            </div>
                            <div style="padding: 10px 12px; display: flex; align-items: center; gap: 10px; background: #f9fafb;">
                                <div style="width: 28px; height: 28px; background: linear-gradient(135deg, #10b981 0%, #059669 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 10px; font-weight: 600;">XY</div>
                                <div>
                                    <div style="font-size: 11px; font-weight: 500; color: #1f2937;">XYZ Corp</div>
                                    <div style="font-size: 9px; color: #6b7280;">info@xyz.com</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            `
        case 'service-select':
            return `
                <div class="tour-demo" style="padding: 12px;">
                    <div style="width: 240px;">
                        <!-- Selected service chip -->
                        <div style="background: white; border: 1px solid #e5e7eb; border-radius: 6px; padding: 8px 12px;">
                            <div style="display: flex; align-items: center; gap: 8px; background: #dbeafe; padding: 6px 10px; border-radius: 4px; width: fit-content;">
                                <span style="font-size: 11px; color: #1d4ed8; font-weight: 500;">4467 - VNIS Enterprise</span>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2"><path d="M6 18L18 6M6 6l12 12"/></svg>
                            </div>
                        </div>
                        <!-- SLA Info preview -->
                        <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 6px; margin-top: 8px; padding: 10px; animation: tour-modal-appear 2s ease-in-out infinite;">
                            <div style="font-size: 10px; font-weight: 600; color: #166534; margin-bottom: 6px; display: flex; align-items: center; gap: 4px;">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                                Thông tin SLA
                            </div>
                            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px; font-size: 9px;">
                                <div><span style="color: #6b7280;">Phản hồi:</span> <span style="color: #166534; font-weight: 500;">30 phút</span></div>
                                <div><span style="color: #6b7280;">Xử lý:</span> <span style="color: #166534; font-weight: 500;">4 giờ</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            `
        case 'priority-select':
            return `
                <div class="tour-demo" style="padding: 12px;">
                    <div style="width: 180px;">
                        <!-- Priority selector -->
                        <div style="background: white; border: 1px solid #e5e7eb; border-radius: 6px; overflow: hidden;">
                            <div style="padding: 10px 12px; background: #fef2f2; border-left: 3px solid #ef4444; display: flex; align-items: center; gap: 8px;">
                                <div style="width: 8px; height: 8px; background: #ef4444; border-radius: 50%; animation: tour-plus-pulse 1s ease-in-out infinite;"></div>
                                <span style="font-size: 11px; font-weight: 600; color: #dc2626;">HIGH</span>
                                <span style="font-size: 9px; color: #ef4444; margin-left: auto;">Xử lý ngay</span>
                            </div>
                            <div style="padding: 10px 12px; border-left: 3px solid transparent; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid #f3f4f6;">
                                <div style="width: 8px; height: 8px; background: #f59e0b; border-radius: 50%;"></div>
                                <span style="font-size: 11px; color: #374151;">MEDIUM</span>
                                <span style="font-size: 9px; color: #6b7280; margin-left: auto;">Trong ngày</span>
                            </div>
                            <div style="padding: 10px 12px; border-left: 3px solid transparent; display: flex; align-items: center; gap: 8px;">
                                <div style="width: 8px; height: 8px; background: #22c55e; border-radius: 50%;"></div>
                                <span style="font-size: 11px; color: #374151;">LOW</span>
                                <span style="font-size: 9px; color: #6b7280; margin-left: auto;">Có thể trì hoãn</span>
                            </div>
                        </div>
                    </div>
                </div>
            `
        case 'assignee-select':
            return `
                <div class="tour-demo" style="padding: 12px;">
                    <div style="width: 220px;">
                        <!-- Multi-select with avatars -->
                        <div style="background: white; border: 1px solid #e5e7eb; border-radius: 6px; padding: 8px;">
                            <div style="display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 8px;">
                                <div style="display: flex; align-items: center; gap: 4px; background: #f3f4f6; padding: 4px 8px; border-radius: 4px;">
                                    <div style="width: 18px; height: 18px; background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 8px;">NV</div>
                                    <span style="font-size: 10px; color: #374151;">Nguyễn Văn A</span>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2"><path d="M6 18L18 6M6 6l12 12"/></svg>
                                </div>
                            </div>
                            <div style="display: flex; align-items: center; gap: 6px; color: #9ca3af; font-size: 10px;">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                                Tìm nhân viên...
                            </div>
                        </div>
                        <!-- Dropdown -->
                        <div style="background: white; border: 1px solid #e5e7eb; border-radius: 6px; margin-top: 4px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); animation: tour-modal-appear 2s ease-in-out infinite;">
                            <div style="padding: 8px 10px; display: flex; align-items: center; gap: 8px; background: #f9fafb;">
                                <div style="width: 22px; height: 22px; background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 8px;">TT</div>
                                <div style="font-size: 10px; color: #374151;">Trần Thị B - Technical</div>
                            </div>
                            <div style="padding: 8px 10px; display: flex; align-items: center; gap: 8px;">
                                <div style="width: 22px; height: 22px; background: linear-gradient(135deg, #10b981 0%, #059669 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 8px;">LV</div>
                                <div style="font-size: 10px; color: #374151;">Lê Văn C - Technical</div>
                            </div>
                        </div>
                    </div>
                </div>
            `
        case 'textarea-input':
            return `
                <div class="tour-demo" style="padding: 12px;">
                    <div style="width: 260px;">
                        <div style="background: white; border: 1px solid #3b82f6; border-radius: 6px; padding: 10px; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);">
                            <div style="font-size: 11px; color: #374151; line-height: 1.5;">
                                <span style="color: #1f2937;">Khách hàng phản ánh không thể truy cập website...</span>
                                <span style="display: inline-block; width: 2px; height: 14px; background: #3b82f6; vertical-align: middle; animation: tour-plus-pulse 0.8s ease-in-out infinite;"></span>
                            </div>
                            <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #f3f4f6; display: flex; justify-content: space-between; align-items: center;">
                                <div style="display: flex; gap: 6px;">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                                </div>
                                <span style="font-size: 9px; color: #9ca3af;">48/2000</span>
                            </div>
                        </div>
                    </div>
                </div>
            `
        case 'datetime-picker':
            return `
                <div class="tour-demo" style="padding: 12px;">
                    <div style="width: 200px;">
                        <!-- Date input -->
                        <div style="background: white; border: 1px solid #3b82f6; border-radius: 6px; padding: 8px 12px; display: flex; align-items: center; gap: 8px; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                            <span style="font-size: 12px; color: #374151;">06/01/2026 14:30</span>
                        </div>
                        <!-- Mini calendar -->
                        <div style="background: white; border: 1px solid #e5e7eb; border-radius: 6px; margin-top: 4px; padding: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); animation: tour-modal-appear 2s ease-in-out infinite;">
                            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
                                <span style="font-size: 10px; font-weight: 600; color: #1f2937;">Tháng 1, 2026</span>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
                            </div>
                            <div style="display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; text-align: center; font-size: 8px;">
                                <div style="color: #9ca3af;">CN</div><div style="color: #9ca3af;">T2</div><div style="color: #9ca3af;">T3</div><div style="color: #9ca3af;">T4</div><div style="color: #9ca3af;">T5</div><div style="color: #9ca3af;">T6</div><div style="color: #9ca3af;">T7</div>
                                <div style="padding: 3px; color: #6b7280;">29</div><div style="padding: 3px; color: #6b7280;">30</div><div style="padding: 3px; color: #6b7280;">31</div><div style="padding: 3px;">1</div><div style="padding: 3px;">2</div><div style="padding: 3px;">3</div><div style="padding: 3px;">4</div>
                                <div style="padding: 3px;">5</div><div style="padding: 3px; background: #3b82f6; color: white; border-radius: 50%; font-weight: 600;">6</div><div style="padding: 3px;">7</div><div style="padding: 3px;">8</div><div style="padding: 3px;">9</div><div style="padding: 3px;">10</div><div style="padding: 3px;">11</div>
                            </div>
                        </div>
                    </div>
                </div>
            `
        case 'credit-transaction-modal':
            return `
                <div class="tour-demo">
                    <div class="tour-demo-modal" style="width: 220px;">
                        <div class="tour-demo-modal-header" style="background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%);">
                            <div class="tour-demo-header-left">
                                <svg class="tour-demo-modal-icon" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
                                </svg>
                                <span class="tour-demo-modal-title" style="color: white;">Thêm/ Hoàn Tiền</span>
                            </div>
                            <svg class="tour-demo-modal-close" style="color: rgba(255,255,255,0.7);" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M6 18L18 6M6 6l12 12"/>
                            </svg>
                        </div>
                        <div class="tour-demo-modal-body">
                            <div class="tour-demo-modal-row">
                                <span class="tour-demo-modal-label">Ví/ Thanh Toán *</span>
                                <div class="tour-demo-modal-input select">Tạo ví mới</div>
                            </div>
                            <div class="tour-demo-modal-row">
                                <span class="tour-demo-modal-label">Loại ký quỹ *</span>
                                <div class="tour-demo-modal-input select">Nạp tiền</div>
                            </div>
                            <div class="tour-demo-modal-row">
                                <span class="tour-demo-modal-label">Số tiền *</span>
                                <div class="tour-demo-modal-input">0</div>
                            </div>
                            <div class="tour-demo-modal-row">
                                <span class="tour-demo-modal-label">Ghi Chú</span>
                                <div class="tour-demo-modal-textarea">Nhập ghi chú...</div>
                            </div>
                        </div>
                        <div class="tour-demo-modal-footer">
                            <button class="tour-demo-modal-btn cancel">Huỷ</button>
                            <button class="tour-demo-modal-btn" style="background: linear-gradient(135deg, #4f46e5 0%, #6366f1 100%); color: white; border: none;">Thêm/ Hoàn Tiền</button>
                        </div>
                    </div>
                </div>
            `

        default:
            return ''
    }
}
